import { useEffect, useSyncExternalStore } from 'react';

import { OrbitWorkspaceTag } from './types';

/** The chat a new conversation is drafted in, before its first question has a session. */
export const ORBIT_NEW_CHAT_KEY = 'new-chat';

export const orbitDemoChatKey = (demoId: string): string => `demo:${demoId}`;

export type OrbitPendingWorkspaces = Readonly<Record<string, OrbitWorkspaceTag>>;

/*
  The Workspace chosen for a chat the server cannot file yet, by session (or draft) key: a chat is
  filed only once an answer lists it. Shared by the full page, the side panel and the provider. It
  lives while any of them is mounted, and empties with the last one.
*/
let pendingByChat: OrbitPendingWorkspaces = {};
let readers = 0;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const snapshot = () => pendingByChat;

const update = (change: (chats: OrbitPendingWorkspaces) => OrbitPendingWorkspaces) => {
  const next = change(pendingByChat);

  if (next !== pendingByChat) {
    pendingByChat = next;
    listeners.forEach(listener => listener());
  }
};

/** Sets the chat's Workspace to file it in, or drops it with `null`. */
export const setOrbitPendingWorkspace = (chatKey: string, tag: OrbitWorkspaceTag | null) =>
  update(chats => {
    if (tag === null) {
      if (chats[chatKey] === undefined) {
        return chats;
      }
      const rest: Record<string, OrbitWorkspaceTag> = { ...chats };
      delete rest[chatKey];
      return rest;
    }
    return chats[chatKey] === tag ? chats : { ...chats, [chatKey]: tag };
  });

/** Moves the draft's Workspace to `toChat`, or drops it when there is none. */
export const leaveOrbitNewChat = (toChat: string | null) =>
  update(chats => {
    const { [ORBIT_NEW_CHAT_KEY]: draft, ...rest } = chats;

    if (draft === undefined) {
      return chats;
    }
    return toChat === null ? rest : { ...rest, [toChat]: draft };
  });

/** Sign-out empties the store, so nothing carries over to the next user. */
export const clearOrbitPendingWorkspaces = () => update(() => ({}));

export const useOrbitPendingWorkspaces = (): OrbitPendingWorkspaces => {
  useEffect(() => {
    readers += 1;
    return () => {
      readers -= 1;
      if (readers === 0) {
        clearOrbitPendingWorkspaces();
      }
    };
  }, []);

  return useSyncExternalStore(subscribe, snapshot);
};
